/**
 * Utility functions for displaying consistent error messages
 */

/**
 * Generate HTML for component-level error messages
 * @param {Object} options - Configuration options
 * @param {string} options.title - The error title/heading
 * @param {string} options.message - The main error message
 * @param {string} options.details - Optional technical details (for developers)
 * @param {boolean} options.showButton - Whether to show the "Create New Journey" button (default: true)
 * @param {string} options.buttonText - Custom text for the button (default: "Try again")
 * @param {string} options.buttonUrl - URL for the button (default: "/")
 * @returns {string} HTML for the error message
 */
function createComponentError(options = {}) {
  const {
    title = 'Oops! We hit a small bump',
    message = 'Please try refreshing the page or creating a new journey to continue.',
    details = '',
    showButton = true,
    buttonText = 'Try again',
    buttonUrl = '/'
  } = options;
  
  // Only show technical details section if details is a non-empty string
  const hasDetails = details && typeof details === 'string' && details.trim() !== '';
  
  // Modified template with mx-0 to override any container padding
  return `
    <div class="p-4 bg-red-50 border border-red-100 rounded max-w-full">
      <div class="flex-grow overflow-hidden">
        <p class="font-medium text-red-700 break-words">${title}</p>
        <p class="text-sm text-gray-600 mt-1 break-words">${message}</p>
        ${hasDetails ? `<details class="mt-2">
          <summary class="text-xs text-gray-500 cursor-pointer">Show technical details</summary>
          <p class="text-xs mt-1 text-gray-500 pl-2 break-words">${details.replace(/\n/g, '<br>')}</p>
          ${showButton ? `
          <div class="mt-4 pl-2">
            <button onclick="window.StartAgain && window.StartAgain.execute()" class="brand-button-compact">
              ${buttonText}
            </button>
          </div>` : ''}
        </details>` : ''}
      </div>
    </div>
  `;
}

/**
 * Display an error message in a container DOM element
 * @param {HTMLElement} container - The element to display the error in
 * @param {Object} options - Error configuration options
 */
function displayErrorInContainer(container, options = {}) {
  if (!container) return;
  
  // Use the existing error HTML generation
  const errorHtml = createComponentError(options);
  
  // Insert the error HTML without adding padding
  container.innerHTML = errorHtml;
}

// Expose utilities globally
window.errorDisplay = {
  createComponentError,
  displayErrorInContainer
};